import { useEffect } from "react";

// HOC used to inject Google Ads / Analytics and optional Google Tag Manager
// scripts for a given component.
//
// - gtmId: Google Ads / Analytics ID (e.g. "AW-XXXXXX")
// - gtmContainerId: Google Tag Manager container ID (e.g. "GTM-XXXXXX")
const withGoogleAds = (Component, { gtmId, gtmContainerId }) => {
  return function WrappedComponent(props) {
    useEffect(() => {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return;
      }

      // Inject Google Ads / Analytics gtag.js (existing behavior)
      if (gtmId) {
        if (!document.querySelector(`script[data-gtag-id="${gtmId}"]`)) {
          const script = document.createElement("script");
          script.src = `https://www.googletagmanager.com/gtag/js?id=${gtmId}`;
          script.async = true;
          script.setAttribute("data-gtag-id", gtmId);
          document.head.appendChild(script);
        }

        if (!document.querySelector("script[data-gtag-inline='true']")) {
          const inlineScript = document.createElement("script");
          inlineScript.setAttribute("data-gtag-inline", "true");
          inlineScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = gtag;
            gtag('js', new Date());
            gtag('config', '${gtmId}');
          `;
          document.head.appendChild(inlineScript);
        }
      }

      // Inject Google Tag Manager container (client request), in addition
      // to the existing Google Ads configuration.
      if (gtmContainerId) {
        if (
          !document.querySelector(
            `script[data-gtm-container="${gtmContainerId}"]`,
          )
        ) {
          const gtmScript = document.createElement("script");
          gtmScript.setAttribute("data-gtm-container", gtmContainerId);
          gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmContainerId}');`;
          document.head.appendChild(gtmScript);
        }

        // Note: In a SPA, the <noscript> tag normally recommended by Google
        // cannot truly cover the "JS disabled" case, because React itself
        // requires JS to render. We still append an equivalent element so
        // that the integration matches the requested markup as closely as
        // possible.
        if (
          !document.querySelector(
            `noscript[data-gtm-container="${gtmContainerId}"]`,
          )
        ) {
          const noscript = document.createElement("noscript");
          noscript.setAttribute("data-gtm-container", gtmContainerId);
          noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmContainerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

          if (document.body) {
            document.body.insertBefore(noscript, document.body.firstChild);
          }
        }
      }
      // Empty dependency array is intentional here: IDs are fixed per
      // wrapped component instance and scripts should only be injected once.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <Component {...props} />;
  };
};

export default withGoogleAds;
